import { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Empty,
  Spin,
  Button,
} from 'antd';
import { ThunderboltOutlined, PictureOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import request from '@/utils/request';
import { formatCents, formatDate } from '@/utils/format';
import { thumb } from '@/utils/image';
import type { CreatorBundleRunDTO } from '@zero/shared';

const STATUS_LABEL: Record<string, { text: string; color: string }> = {
  pending: { text: '准备中', color: 'default' },
  running: { text: '生成中', color: 'processing' },
  success: { text: '已完成', color: 'success' },
  failed: { text: '失败', color: 'error' },
};

const UsageListPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<CreatorBundleRunDTO[]>([]);
  const [urlMap, setUrlMap] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await request.get<{ items: CreatorBundleRunDTO[] }>('/creator/bundle-runs');
      const data = (res.data as unknown as { items: CreatorBundleRunDTO[] }).items;
      setItems(data);
      const ids = new Set<number>();
      for (const r of data) {
        if (r.bundleCoverImageId) ids.add(r.bundleCoverImageId);
        for (const rec of r.records) for (const id of rec.outputImageIds) ids.add(id);
      }
      if (ids.size > 0) {
        const imgRes = await request.get<{ items: { id: number; accessUrl: string }[] }>(
          `/creator/images?ids=${Array.from(ids).join(',')}`,
        );
        const map = new Map<number, string>();
        for (const im of (imgRes.data as unknown as { items: { id: number; accessUrl: string }[] }).items) {
          map.set(im.id, im.accessUrl);
        }
        setUrlMap(map);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            套图调用
          </Typography.Title>
          <Typography.Text type="secondary">
            创作者亲测自己的套图 — 用实验金付费生成，结果按 run 聚合在此
          </Typography.Text>
        </div>
        <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => navigate('/bundles')}>
          去我的套图选一个运行
        </Button>
      </Space>

      {items.length === 0 ? (
        <Card>
          <Empty
            className="brand-empty"
            description="还没有试跑记录。到『组合套图』选一个已发布的套图点击运行"
          >
            <Button type="primary" onClick={() => navigate('/bundles')}>
              去套图列表
            </Button>
          </Empty>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {items.map((run) => {
            const label = STATUS_LABEL[run.status] ?? STATUS_LABEL.pending;
            const firstOutput = run.records
              .flatMap((r) => r.outputImageIds)
              .find((id) => urlMap.has(id));
            const coverSrc = firstOutput
              ? urlMap.get(firstOutput)
              : run.bundleCoverImageId
                ? urlMap.get(run.bundleCoverImageId)
                : null;
            return (
              <Col key={run.id} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  onClick={() => navigate(`/usage/${run.id}`)}
                  style={{ overflow: 'hidden' }}
                  cover={
                    <div
                      style={{
                        position: 'relative',
                        height: 160,
                        background: coverSrc
                          ? undefined
                          : 'linear-gradient(135deg, #FEF3C7 0%, #F6F3EC 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#B45309',
                        overflow: 'hidden',
                      }}
                    >
                      {coverSrc ? (
                        <img
                          src={thumb(coverSrc, 200)}
                          alt={run.bundleTitle}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <PictureOutlined style={{ fontSize: 36 }} />
                      )}
                      <Tag
                        color={label.color}
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          margin: 0,
                          boxShadow: '0 1px 3px rgba(0,0,0,.12)',
                        }}
                      >
                        {label.text}
                      </Tag>
                    </div>
                  }
                >
                  <Card.Meta
                    title={<span>{run.bundleTitle}</span>}
                    description={
                      <Space direction="vertical" size={4}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {run.records.filter((r) => r.status === 'success').length}
                          {' / '}
                          {run.records.length} 张完成
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          扣费 {formatCents(run.chargedCents)}
                          {run.refundedCents > 0 && ` · 退款 ${formatCents(run.refundedCents)}`}
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {formatDate(run.createdAt)}
                        </Typography.Text>
                      </Space>
                    }
                  />
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </Space>
  );
};

export default UsageListPage;
